/*******************************************************************************
 * @ngdoc  provider
 * @name   AppDataProvider
 * @module csm.books.InitialSetup.services
 * @description
 * Configuration for AppData service. Add properties to `data` to make them
 * available in the service.
 *******************************************************************************/
/* @ngInject */ export function AppDataProvider($injector) {
  this.$get = $injector.invoke(service, this);

  /**
   * @ngdoc property
   * @name  AppDataProvider#data
   * @description
   * Properties added to AppData service. AppData service is sealed so it's not
   * possible to add properties to the service unless they are added in the
   * config block as `writable`.
   */
  this.data = Object.defineProperties(
    {},
    {
      /**
       * @ngdoc property
       * @name  AppData#accountTypes
       * @description
       * List of `AccountType` objects from server. Must me assigned in `AppServices.loadSetup`.
       */
      accountTypes: { writable: true },

      /**
       * @ngdoc property
       * @name  AppData#setupData
       * @description
       * `SetupData` object loaded from and saved to the server. Must be assigned in `AppServices.loadSetup`.
       */
      setupData: { writable: true }
    }
  );
}

/***************************************************************************
 * service */
/**
 * @ngdoc  service
 * @name   AppData
 * @module csm.books.InitialSetup.services
 * @description
 * Setup application global data.
 ***************************************************************************/
/* @ngInject */ function service($injector, Tools) {
  return function () {
    var $svc = this;
    var $global = {};
    var $svcs = { $svc: $svc, $global: $global };

    /*-----------------------------*/
    /* Load local service methods. */
    /*-----------------------------*/
    Object.assign($global, {
      calcFundBalance: $injector.invoke(calcFundBalance, null, $svcs),
      calcNetIncome: $injector.invoke(calcNetIncome, null, $svcs),
      periodStartDate: $injector.invoke(periodStartDate, null, $svcs),
      totalNetIncome: $injector.invoke(totalNetIncome, null, $svcs)
    });

    /*----------------------------------*/
    /* Load service property accessors. */
    /*----------------------------------*/
    Object.defineProperties($svc.data, {
      /**
       * @ngdoc property
       * @name  AppData#accounts
       * @description
       * `AccountList` of `SetupAccount` objects to send to the server. Setter writes to `setupData.accounts`.
       * Must be set in `AppServices.loadSetup` and can be used as `ArrayCollection` source so any objects
       * added will be assigned a new ID.
       */
      accounts: {
        get: function () {
          return this.setupData.accounts;
        },
        set: function (v) {
          this.setupData.accounts = v;
        }
      },

      /**
       * @ngdoc property
       * @name  AppData#orgInfo
       * @description
       * Accessor for `setupData.org`.
       */
      orgInfo: {
        get: function () {
          return this.setupData.org;
        },
        set: function (v) {
          this.setupData.org = v;
        }
      },

      /*---------------------------------------------*/
      /* Filtered account lists.                     */
      /*                                             */
      /* Note: These are not ArrayCollection objects */
      /* and can't be used for adding new accounts.  */
      /*---------------------------------------------*/
      /**
       * @ngdoc property
       * @name  AppData#bankAccounts
       * @description
       * List of `SetupBankAccount` objects filtered from `AppData.accounts`.
       * **Note:** Objects added or removed **will not change** `AppData.accounts`.
       */
      bankAccounts: {
        get: function () {
          return this.accounts.filter(function (acct) {
            return acct.isBankAccount;
          });
        }
      },

      /**
       * @ngdoc property
       * @name  AppData#bsAccounts
       * @description
       * List of Bank Statement `SetupAccount` objects filtered from `AppData.accounts`.
       * Used for setting opening/current balances and creating opening journal entries.
       * **Note:** Objects added or removed **will not change** `AppData.accounts`.
       */
      bsAccounts: {
        get: function () {
          return this.accounts.filter(function (acct) {
            return acct.isBSAccount;
          });
        }
      },

      /**
       * @ngdoc property
       * @name  AppData#bsAccounts
       * @description
       * List of Income/Expense `SetupAccount` objects filtered from `AppData.accounts`.
       * Used for distributing Net Income and creating opening journal entries.
       * **Note:** Objects added or removed **will not change** `AppData.accounts`.
       */
      ieAccounts: {
        get: function () {
          return this.accounts.filter(function (acct) {
            return acct.isIEAccount;
          });
        }
      },

      /*-------------------------------------------------------------*/
      /* Calculations based on Opening Date and Period Start Months. */
      /*-------------------------------------------------------------*/
      setupFYStartMonth: {
        get: function () {
          return this.orgInfo.fyMonth - 1;
        }
      },
      setupTYStartMonth: {
        get: function () {
          return this.orgInfo.tyMonth - 1;
        }
      },
      fyStartDate: {
        get: function () {
          return $global.periodStartDate(
            this.setupFYStartMonth,
            1,
            this.orgInfo.openDate
          );
        }
      },
      tyStartDate: {
        get: function () {
          return $global.periodStartDate(
            this.setupTYStartMonth,
            1,
            this.orgInfo.openDate
          );
        }
      },
      fyOnly: {
        get: function () {
          return this.fyStartDate.getTime() === this.tyStartDate.getTime();
        }
      },
      fyLater: {
        get: function () {
          return this.fyStartDate.getTime() >= this.tyStartDate.getTime();
        }
      },
      needFY: {
        get: function () {
          return this.fyStartDate.getTime() !== this.orgInfo.openDate.getTime();
        }
      },
      needTY: {
        get: function () {
          return (
            this.tyStartDate.getTime() !== this.orgInfo.openDate.getTime() &&
            !this.fyOnly
          );
        }
      },

      openingFundBalance: {
        get: function () {
          return $global.calcFundBalance();
        }
      },

      fyCalcFundBalance: {
        get: function () {
          return $global.calcFundBalance(true);
        }
      },
      fyCalcNetIncome: {
        get: function () {
          return $global.calcNetIncome(true);
        }
      },
      fyTotalNetIncome: {
        get: function () {
          return $global.totalNetIncome(true);
        }
      },

      tyCalcFundBalance: {
        get: function () {
          return $global.calcFundBalance(false);
        }
      },
      tyCalcNetIncome: {
        get: function () {
          return $global.calcNetIncome(false);
        }
      },
      tyTotalNetIncome: {
        get: function () {
          return $global.totalNetIncome(false);
        }
      },

      fyFundBalanceAdj: {
        get: function () {
          return Tools.roundMoney(this.fyCalcNetIncome - this.fyTotalNetIncome);
        }
      },
      tyFundBalanceAdj: {
        get: function () {
          return Tools.roundMoney(this.tyCalcNetIncome - this.tyTotalNetIncome);
        }
      },
      fyAdjFundBalance: {
        get: function () {
          return Tools.roundMoney(
            this.fyCalcFundBalance + this.fyFundBalanceAdj
          );
        }
      },
      tyAdjFundBalance: {
        get: function () {
          return Tools.roundMoney(
            this.tyCalcFundBalance + this.tyFundBalanceAdj
          );
        }
      }
    });

    /*-----------------*/
    /* Return service. */
    /*-----------------*/
    return Object.seal($svc.data);
  };
}

/***************************************************************************
 * $global.calcFundBalance */
/**
 * Calculates Fund Balance (Retained Earnings) from Balance Sheet account
 * opening balances. Retained Earnings = debit account opening balances less
 * credit account opening balances.
 *
 * @param  {boolean} forFiscalYear  `true`=Fiscal Year, `false`=Tax Year, null=Open Date.
 * @return {Number}  Calculated Fund Balance.
 ***************************************************************************/
/* @ngInject */ function calcFundBalance($svc, SERVER, Tools) {
  return function (forFiscalYear) {
    return Tools.roundMoney(
      $svc.data.bsAccounts.reduce(function (total, acct) {
        var amount =
          forFiscalYear === true
            ? acct.fyOpeningBalance
            : forFiscalYear === false
              ? acct.tyOpeningBalance
              : acct.currentBalance;
        return (
          total +
          (acct.side === SERVER.BooksDBConstants.AS_DEBIT ? amount : -amount)
        );
      }, 0)
    );
  };
}

/***************************************************************************
 * $global.calcNetIncome */
/**
 * Calculates Change in Fund Balance (Net Income) from Balance Sheet account
 * current balances. Net Income = balance change (current less opening) for
 * debit accounts less change for credit accounts.
 *
 * @param  {boolean} forFiscalYear  `true`=Fiscal Year, `false`=Tax Year.
 * @return {Number}  Calculated Change in Fund Balance.
 ***************************************************************************/
/* @ngInject */ function calcNetIncome($svc, SERVER, Tools) {
  return function (forFiscalYear) {
    return Tools.roundMoney(
      $svc.data.bsAccounts.reduce(function (total, acct) {
        var opening = forFiscalYear
          ? acct.fyOpeningBalance
          : acct.tyOpeningBalance;
        var current = forFiscalYear
          ? acct.fyCurrentBalance
          : acct.tyCurrentBalance;
        var amount = current - opening;
        return (
          total +
          (acct.side === SERVER.BooksDBConstants.AS_DEBIT ? amount : -amount)
        );
      }, 0)
    );
  };
}

/*******************************************************************************
 * $global.periodStartDate */
/**
 * Calculate FY/TY Start Date for a given date. If date < start month/day, period
 * starts in previous year, else current year.
 *
 * @param {integer} pyear   Period start year.
 * @param {integer} pmonth  Period start month.
 * @param {Date}    today   Date in period for which to find the start date.
 * @return                  Start date of period containing `today`.
 *******************************************************************************/
/* @ngInject */ function periodStartDate() {
  return function (pmonth, pday, today) {
    var pstart = new Date(today.getFullYear(), pmonth, pday);
    var pyear =
      today < pstart ? pstart.getFullYear() - 1 : pstart.getFullYear();
    return new Date(pyear, pmonth, pday);
  };
}

/***************************************************************************
 * $global.totalNetIncome */
/**
 * Accumulates Change in Fund Balance (Net Income) from Income Statement
 * account current balances. Net Income = Total revenue account less expense
 * account current balances.
 *
 * @param  {boolean} forFiscalYear  `true`=Fiscal Year, `false`=Tax Year.
 * @return {Number}  Accumulated Change in Fund Balance.
 ***************************************************************************/
/* @ngInject */ function totalNetIncome($svc, SERVER, Tools) {
  return function (forFiscalYear) {
    return Tools.roundMoney(
      $svc.data.ieAccounts.reduce(function (total, acct) {
        var amt = forFiscalYear ? acct.fyCurrentBalance : acct.tyCurrentBalance;
        return (
          total + (acct.side === SERVER.BooksDBConstants.AS_CREDIT ? amt : -amt)
        );
      }, 0)
    );
  };
}
